import {
  styled,
  Switch,
  Table,
  TableInstance,
} from "@monolith-forensics/monolith-ui";
import type {
  ButtonProps,
  ColumnProps,
  DropDownItem,
} from "@monolith-forensics/monolith-ui";
import type { WithTestId } from "@/types/testid";
import { useLayoutEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import {
  useCaseFileFilterDefinitions,
  useCaseFiles,
  useCaseFilesEventHandlers,
  useFileUploader,
} from "../Hooks";
import { CaseFileDefinitions } from "../Definitions";
import { useGetInfiniteFiles } from "../files.query";
import { File, GetFilesQuery } from "../files.types";
import {
  FolderPlusIcon,
  FolderUpIcon,
  InfoIcon,
  SendIcon,
  UploadIcon,
} from "lucide-react";
import { ActionMenu } from "@/Features/Tables";
import {
  resolveFilterState,
  useClearTransientTableState,
  useTableState,
} from "@/Features/_shared";
import { FilesAPI } from "../files.api";
import { ManagedActionHint } from "@/Features/EvidenceFiles/ManagedActionHint";
import {
  canDropInto,
  isManagedFolder,
  managedFolderMessage,
  MANAGED_NEW_FOLDER_MESSAGE,
} from "@/Features/EvidenceFiles/evidence-files.utils";
import { isInternalDrag } from "../utils/dragAndDrop";
import { getDateFormat } from "@/utils/date-format";

const PAGE_SIZE = 100;

const StyledContainer = styled.div`
  position: relative;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;

  .inner-cell-content {
    width: 100%;
  }
`;

const StyledOverlay = styled.div`
  position: absolute;
  top: 30px;
  left: 0;
  width: 100%;
  height: calc(100% - 30px);

  display: flex;
  flex-direction: column;
  flex: 1 1 auto;

  opacity: 0;
  visibility: hidden;
  pointer-events: none;

  transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out,
    outline 0.2s ease-in-out;

  border-radius: 2px;
  outline: 2px solid transparent;

  &.dragging-over {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    outline: 2px solid ${({ theme }) => theme.palette.primary.main};
  }
`;

export type CaseFilesTableRef = {
  refresh: () => void;
  clearSearch: () => void;
};

export const CaseFilesTable: React.FC<{
  tableRef?: React.MutableRefObject<CaseFilesTableRef>;
  children?: React.ReactNode;
  query: GetFilesQuery;
  onCreateFolder?: (e: React.MouseEvent) => void;
  onUploadFile?: (e: React.MouseEvent) => void;
  onUploadFolder?: (e: React.MouseEvent) => void;
  onLoaded?: () => void;
  onActionButtonClick?: (rowData: File) => void;
  onDelete?: (rowData: File) => void;
  onView?: (rowData: File) => void;
  onDownload?: (rowData: File) => void;
  onUpdate?: (rowData: File) => void;
  onShowDetails?: (rowData: File) => void;
  onShareViaRelay?: (rowData: File) => void;
  onSearch?: (search: string) => void;
  readOnly?: boolean;
  stateStoreKey?: string;
  /**
   * False while there's nothing to list yet (an evidence item with no folder,
   * ENG-2156): the table shows empty without fetching, and `refresh` is a
   * noop so a listener can't fetch the case root by mistake.
   */
  enabled?: boolean;
  /**
   * Make a desktop drop inert (no overlay, nothing queued), for a user who
   * can't upload here. The drop still calls `preventDefault` so the browser
   * doesn't open the file.
   */
  dropDisabled?: boolean;
  /** Columns a surface adds after the Case Files ones (ENG-2291). */
  extraColumns?: ColumnProps[];
}> = ({
  tableRef,
  children,
  query = {},
  stateStoreKey = "case:files:table",
  enabled = true,
  dropDisabled = false,
  extraColumns = [],
  onCreateFolder,
  onUploadFile,
  onUploadFolder,
  onLoaded,
  onActionButtonClick,
  onDelete,
  onView,
  onDownload,
  onUpdate,
  onShowDetails,
  onShareViaRelay,
  onSearch,
  readOnly = false,
}) => {
  const { updateHashSettings, hashSettings } = useFileUploader();
  useCaseFilesEventHandlers({
    onFilesAdded: () => {
      setDraggingOver(false); // Reset dragging state when files are added; hides overly border when dropping files onto internal folders
    },
    onUploadRejected: () => {
      setDraggingOver(false); // A drop onto a folder row stops propagation, so the table's onDrop never resets this when the batch is refused
    },
  });
  const { handleItemDrop, currentFolder, surface, api } = useCaseFiles();
  // No case and no folders (ENG-2291): no Recursive switch and no export,
  // which lists the case's files.
  const caseless = surface === "asset-attachments";
  // Inside the managed Evidence Files tree (ENG-2133): no new folders, and
  // no rows moved in from elsewhere. Desktop file uploads are left alone.
  const inManagedTree = isManagedFolder(currentFolder);
  const refusesDrop = (e: React.DragEvent) =>
    isInternalDrag(e) && !canDropInto(currentFolder, { internal: true });
  const internalTableRef = useRef<TableInstance>();

  const recursiveSettings = JSON.parse(
    localStorage.getItem("files:recursive") || "false"
  );

  const [recursive, setRecursive] = useState<boolean>(
    recursiveSettings.recursive
  );

  const [draggingOver, setDraggingOver] = useState<boolean>(false);

  // The stateStoreKey is global so the column layout follows the user across
  // cases, but search/filter typed in one case must not restore in another.
  useClearTransientTableState(stateStoreKey);
  const tableState = useTableState<GetFilesQuery>(query, stateStoreKey);

  const _query = {
    ...tableState.resolvedQuery,
    page_size: PAGE_SIZE,
    recursive,
    hidden: false,
  };

  const {
    data,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    isFetched,
  } = useGetInfiniteFiles(_query, { enabled, api });
  // Only a live listing: a disabled query still gets placeholderData, which
  // is the last listing this table showed (another item's folder, say).
  const records = enabled
    ? data?.pages?.reduce<File[]>((acc, page) => {
        return [...acc, ...page.data];
      }, []) || []
    : [];

  const debouncedFetchNextPage = useDebouncedCallback(() => {
    fetchNextPage();
  }, 50);

  const debouncedSearch = useDebouncedCallback((value) => {
    tableState.setSearchState(value);
    onSearch?.(value);
  }, 300);

  const handleExportTable = (option: string) => {
    let columns = (internalTableRef.current?.columnState ?? [])
      .filter((c) => c.dataField !== "Action")
      .map((c) => ({
        dataField: c.dataField,
        header: c.caption || c.dataField,
        caption: c.caption || c.dataField,
        visible: c.visible,
        dataType: c.dataType,
      }));

    if (option === "export:visible") {
      columns = columns.filter((c) => c.visible !== false);
    }

    FilesAPI.exportFilesList({
      query: _query,
      columns,
      date_format: getDateFormat({ isMoment: true, includeTime: true }),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }).then((res) => {
      const { signedUrl, filename } = res;
      const el = document.createElement("a");
      el.href = signedUrl.replace(
        "http://localhost:3000",
        "http://localhost:3001"
      );
      el.download = filename;
      el.click();
      // remove snackbar
      el.remove();
    });
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation(); // prevent the drop from being handled by the table

    handleItemDrop(e);
  };

  const updateRecursiveSettings = (settings: { recursive: boolean }) => {
    localStorage.setItem("files:recursive", JSON.stringify(settings));
  };

  const loading = enabled && (isLoading || !isFetched);

  if (tableRef) {
    tableRef.current = {
      refresh: () => {
        if (enabled) refetch();
      },
      clearSearch: () => {
        tableState.setSearchState("");
        internalTableRef.current?.clearSearch();
      },
    };
  }

  useLayoutEffect(() => {
    if (isFetched) {
      onLoaded?.();
    }
  }, [isFetched, onLoaded]);

  const filters = useCaseFileFilterDefinitions();

  // The "+ New" menu carries only the actions the caller handles: the
  // evidence item Files tab has no folders, so it passes no onCreateFolder or
  // onUploadFolder (ENG-2156).
  const newMenuOptions: DropDownItem[] = [
    ...(onCreateFolder
      ? [
          {
            label: "New Folder",
            value: "new-folder",
            leftSection: <FolderPlusIcon size={14} color="orange" />,
            onClick: onCreateFolder,
            ...(inManagedTree
              ? {
                  disabled: true,
                  rightSection: (
                    <ManagedActionHint
                      message={MANAGED_NEW_FOLDER_MESSAGE}
                      data-testid="files-new-folder-managed-hint"
                    />
                  ),
                }
              : {}),
          },
        ]
      : []),
    ...(onUploadFile
      ? [
          {
            label: "Upload File",
            value: "upload-file",
            leftSection: <UploadIcon size={14} color="lightblue" />,
            onClick: onUploadFile,
          },
        ]
      : []),
    ...(onUploadFolder
      ? [
          {
            label: "Upload Folder",
            value: "upload-folder",
            leftSection: <FolderUpIcon size={14} color="lightblue" />,
            onClick: onUploadFolder,
            // A folder upload creates folders, which the managed tree refuses
            ...(inManagedTree
              ? {
                  disabled: true,
                  rightSection: (
                    <ManagedActionHint
                      message={MANAGED_NEW_FOLDER_MESSAGE}
                      data-testid="files-upload-folder-managed-hint"
                    />
                  ),
                }
              : {}),
          },
        ]
      : []),
  ];

  return (
    <StyledContainer
      className="case-files-table"
      onDragOver={(e) => {
        e.preventDefault();
        if (refusesDrop(e)) {
          e.dataTransfer.dropEffect = "none";
          return;
        }
        if (!dropDisabled) setDraggingOver(true);
      }}
      onDragEnter={(e) => {
        if (refusesDrop(e)) return;
        if (!dropDisabled) setDraggingOver(true);
      }}
      onDragLeave={() => setDraggingOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        if (dropDisabled) return;
        console.log("Item dropped onto table plane");
        setDraggingOver(false);
        handleDrop(e);
      }}
      onMouseLeave={() => setDraggingOver(false)}
    >
      <StyledOverlay
        className={`case-files-table-overlay ${
          draggingOver ? "dragging-over" : ""
        }`}
      />
      <Table
        tableInstanceRef={internalTableRef}
        columnProps={[
          {
            dataField: "Action",
            width: 50,
            render: ({ rowData }: { rowData: File }) => {
              return (
                <ActionMenu
                  item={rowData}
                  items={[
                    {
                      label: "Show Details",
                      value: "show-details",
                      onClick: () => onShowDetails?.(rowData),
                      leftSection: <InfoIcon size={14} color="lightblue" />,
                    },
                    // Only where the caller can share: Relay shares are
                    // case-scoped (ENG-2291).
                    ...(onShareViaRelay
                      ? [
                          {
                            label: "Share via Relay",
                            value: "share-relay",
                            onClick: () => onShareViaRelay(rowData),
                            leftSection: (
                              <SendIcon size={14} color="lightblue" />
                            ),
                          },
                        ]
                      : []),
                  ]}
                  // Edit and Delete only where the caller handles them: the
                  // asset Attachments tab (ENG-2291) has no edit, and no
                  // delete without the permission.
                  onDelete={
                    !readOnly && onDelete ? () => onDelete(rowData) : undefined
                  }
                  onView={() => onView?.(rowData)}
                  onDownload={() => onDownload?.(rowData)}
                  onEdit={onUpdate ? () => onUpdate(rowData) : undefined}
                  // The Files API refuses a rename, description or delete on
                  // the managed folders (ENG-2130); say so up front.
                  disabledReasons={
                    isManagedFolder(rowData)
                      ? {
                          edit: (
                            <ManagedActionHint
                              message={managedFolderMessage(
                                "renamed or edited"
                              )}
                              data-testid="files-edit-managed-hint"
                            />
                          ),
                          delete: (
                            <ManagedActionHint
                              message={managedFolderMessage("deleted")}
                              data-testid="files-delete-managed-hint"
                            />
                          ),
                        }
                      : undefined
                  }
                />
              );
            },
          },
          ...CaseFileDefinitions,
          ...extraColumns,
        ]}
        data={records}
        loading={loading}
        virtualized={true}
        manualSearch={true}
        manualSorting={true}
        manualExport={true}
        manualFiltering={true}
        onScrollToBottom={debouncedFetchNextPage}
        onColumnHeaderClick={({ sort }) =>
          tableState.setSortState(sort || undefined)
        }
        stateStorage={{
          enabled: !!stateStoreKey,
          type: "localStorage",
          key: stateStoreKey,
        }}
        enableSorting={true}
        enableColumnReorder={true}
        enableColumnResize={true}
        tableMenuOptions={{
          enabled: true,
          addButtonOptions: {
            enabled: newMenuOptions.length > 0,
            label: "+ New",
            type: "menu",
            menuOptions: {
              // buttonProps replaces TableMenu's defaults, so color/size restated.
              buttonProps: {
                color: "primary",
                size: "xxs",
                "data-testid": "files-new-button",
              } as WithTestId<ButtonProps>,
              data: newMenuOptions,
              size: "xs",
            },
          },
          filterOptions: {
            enabled: true,
            filterDefinitions: filters,
            onFilterChange: (filterQuery) => {
              tableState.setFilterState(resolveFilterState(filterQuery));
            },
          },
          searchOptions: {
            enabled: true,
            placeholder: "Search Files",
            onSearch: debouncedSearch,
          },
          exportOptions: {
            // Off with the listing: with no parent the query would export
            // the whole case.
            enabled: enabled && !caseless,
            onExport: handleExportTable,
          },
          columnSelectorOptions: {
            enabled: true,
          },
          children: (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginLeft: 5,
                gap: 10,
              }}
            >
              {!caseless && (
                <Switch
                  defaultValue={recursive}
                  label="Recursive"
                  labelPosition="right"
                  description="View the contents of the selected folder and all nested folders and files in the tree"
                  size="xs"
                  onChange={(value) => {
                    setRecursive(value);
                    updateRecursiveSettings({ recursive: value });
                  }}
                />
              )}
              <Switch
                defaultValue={hashSettings.hashingEnabled}
                label="Hash Uploads"
                labelPosition="right"
                description="Calculates MD5, SHA1, and SHA256 hashes for uploaded files - this may increase upload time if you have high upload bandwidth"
                size="xs"
                onChange={(value) => {
                  updateHashSettings({ hashingEnabled: value });
                }}
              />
            </div>
          ),
        }}
      >
        {children}
      </Table>
    </StyledContainer>
  );
};
